import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Share2, Copy, Check, MessageCircle, Facebook, Instagram } from "lucide-react";

const canNativeShare = () => typeof navigator !== "undefined" && typeof navigator.share === "function";

// Compartir nativo (iOS/Android/navegadores) + respaldo con WhatsApp, Facebook y copiar enlace.
export function NativeShare({ title, text, url, testid = "native-share", showFallbackAlways = false, compact = false, tiles = false, imageUrl = null }) {
  const [copied, setCopied] = useState(false);
  const [showFallback, setShowFallback] = useState(!canNativeShare() || showFallbackAlways);

  const share = async () => {
    if (!canNativeShare()) { setShowFallback(true); return; }
    try {
      await navigator.share({ title, text, url });
    } catch (err) {
      if (err?.name !== "AbortError") setShowFallback(true);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("No se pudo copiar el enlace"); }
  };
  const msg = encodeURIComponent(`${text ? `${text} ` : ""}${url}`);

  // Instagram no permite compartir enlaces desde la web: en móvil se comparte la imagen con la hoja nativa
  // (el usuario elige Instagram); en desktop se descarga la imagen y se copia texto + enlace.
  const instagram = async () => {
    const full = `${text ? `${text} ` : ""}${url}`;
    try {
      const blob = imageUrl ? await (await fetch(imageUrl)).blob() : null;
      const file = blob ? new File([blob], "subasta-hittcg.jpg", { type: "image/jpeg" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        try { await navigator.clipboard.writeText(full); } catch { /* sin portapapeles */ }
        await navigator.share({ files: [file], title, text: full });
        toast.success("Elige Instagram. El enlace quedó copiado para pegarlo en el sticker de enlace.");
        return;
      }
      await navigator.clipboard.writeText(full);
      if (blob) {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "subasta-hittcg.jpg";
        a.click();
        URL.revokeObjectURL(a.href);
      }
      toast.success("Imagen descargada y enlace copiado. Súbela a tu historia de Instagram y pega el enlace en el sticker.");
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("No se pudo preparar la imagen para Instagram");
    }
  };

  if (tiles) {
    const t = "h-12 rounded-[14px] flex items-center justify-center gap-1.5 text-sm font-bold transition-colors";
    return (
      <div className="space-y-2" data-testid={testid}>
        {canNativeShare() && (
          <button type="button" onClick={share} data-testid={`${testid}-btn`} className="sx-btn w-full h-12 flex items-center justify-center gap-2 text-sm">
            <Share2 size={15} /> Compartir
          </button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-whatsapp`}
             className={`${t} text-[#34D399] bg-[#34D399]/10 border border-[#34D399]/35 hover:bg-[#34D399]/15`}>WhatsApp</a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-facebook`}
             className={`${t} text-sky-300 bg-sky-500/10 border border-sky-400/35 hover:bg-sky-500/15`}>Facebook</a>
          {imageUrl && (
            <button type="button" onClick={instagram} data-testid={`${testid}-instagram`}
                    className={`${t} text-pink-300 bg-pink-500/10 border border-pink-400/35 hover:bg-pink-500/15`}>
              <Instagram size={15} /> Instagram
            </button>
          )}
          <button type="button" onClick={copy} data-testid={`${testid}-copy`}
                  className={`${t} text-white border border-white/20 hover:border-white/40`}>
            {copied ? <><Check size={14} className="text-[#34D399]" /> Copiado</> : "Copiar link"}
          </button>
        </div>
      </div>
    );
  }

  if (compact) {
    const ico = "h-9 w-9 rounded-md bg-[#171717] border border-[#262626] flex items-center justify-center hover:border-neutral-400";
    return (
      <div className="flex items-center gap-2" data-testid={testid}>
        {canNativeShare() && (
          <button type="button" onClick={share} data-testid={`${testid}-btn`} aria-label="Compartir"
                  className="h-9 px-3 rounded-md bg-[#EF4444] hover:bg-[#DC2626] text-white text-xs font-semibold flex items-center gap-1.5">
            <Share2 size={14} /> Compartir
          </button>
        )}
        <a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" data-testid={`${testid}-whatsapp`} className={ico}>
          <MessageCircle size={15} className="text-emerald-400" />
        </a>
        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid={`${testid}-facebook`} className={ico}>
          <Facebook size={15} className="text-sky-400" />
        </a>
        <button type="button" onClick={copy} aria-label="Copiar enlace" data-testid={`${testid}-copy`} className={ico}>
          {copied ? <Check size={15} className="text-emerald-400" /> : <Copy size={15} />}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testid}>
      {canNativeShare() && (
        <Button type="button" onClick={share} data-testid={`${testid}-btn`} className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white">
          <Share2 size={16} className="mr-2" /> Compartir
        </Button>
      )}
      {showFallback && (
        <div className="grid grid-cols-3 gap-2" data-testid={`${testid}-fallback`}>
          <a href={`https://wa.me/?text=${msg}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-whatsapp`}
             className="h-10 rounded-md bg-[#171717] border border-[#262626] hover:border-emerald-500 flex items-center justify-center gap-1 text-xs">
            <MessageCircle size={14} className="text-emerald-400" /> WhatsApp
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-facebook`}
             className="h-10 rounded-md bg-[#171717] border border-[#262626] hover:border-sky-500 flex items-center justify-center gap-1 text-xs">
            <Facebook size={14} className="text-sky-400" /> Facebook
          </a>
          <button type="button" onClick={copy} data-testid={`${testid}-copy`}
                  className="h-10 rounded-md bg-[#171717] border border-[#262626] hover:border-neutral-400 flex items-center justify-center gap-1 text-xs">
            {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />} {copied ? "Copiado" : "Copiar"}
          </button>
        </div>
      )}
      {canNativeShare() && !showFallback && (
        <button type="button" onClick={() => setShowFallback(true)} className="w-full text-[11px] text-neutral-500 hover:text-white" data-testid={`${testid}-more`}>
          Más opciones (WhatsApp, Facebook, copiar)
        </button>
      )}
    </div>
  );
}

export default NativeShare;
